import { config } from 'src/config';
import { AppRoutes } from 'src/routes';
import { helpCenterNeighboprsUrl } from 'src/shared/utils/constants';

const {
  NEIGHBORS_COMMUNITY_GUIDELINES_URL,
  NH_APP_ATTRIBUTION_URL,
  RING_APP_ATTRIBUTION_URL,
} = config;

export interface NavItem {
  /** Destination URL: an absolute external URL or an internal app route. */
  url: string;
  /** Whether the item is rendered. Defaults to true. */
  visible?: boolean;
  /** Label translation key in the components.navbar namespace. */
  labelKey?: string;
}

export type ResolvedNavItem = Required<Pick<NavItem, 'url' | 'visible'>> &
  Pick<NavItem, 'labelKey'>;

export type NavItemKey =
  | 'getTheApp'
  | 'learnMore'
  | 'helpCenter'
  | 'deals'
  | 'communityGuidelines';

export type LocalizedNavConfig = Record<NavItemKey, ResolvedNavItem>;

const defaultNavConfig: Record<NavItemKey, NavItem> = {
  getTheApp: { url: NH_APP_ATTRIBUTION_URL, labelKey: 'get-the-app' },
  learnMore: { url: NH_APP_ATTRIBUTION_URL, labelKey: 'learn-more' },
  helpCenter: { url: helpCenterNeighboprsUrl, labelKey: 'help-center' },
  deals: { url: AppRoutes.Deals, labelKey: 'deals' },
  communityGuidelines: { url: NEIGHBORS_COMMUNITY_GUIDELINES_URL },
};

// Per-locale overrides are partial: only specify what differs from the
// en-US defaults. Adding a locale is a single new entry here.
const localeNavOverrides: Record<
  string,
  Partial<Record<NavItemKey, Partial<NavItem>>>
> = {
  'en-GB': {
    getTheApp: { url: RING_APP_ATTRIBUTION_URL },
    learnMore: { url: 'https://en-uk.ring.com/pages/neighbours' },
    helpCenter: {
      url: 'https://ring.com/gb/en/support/categories/neighbors-app-help?page=1',
    },
    deals: {
      url: 'https://www.amazon.co.uk/stores/Ring/page/B81173C3-015D-4766-A044-6817797BBC1F',
    },
    communityGuidelines: {
      url: 'https://ring.com/gb/en/support/articles/q2p3w/Neighbors-by-Ring-Community-Guidelines',
    },
  },
};

export const isExternalNavUrl = (url: string) => /^https?:\/\//.test(url);

export const getLocalizedNavConfig = (locale?: string): LocalizedNavConfig => {
  const overrides = (locale && localeNavOverrides[locale]) || {};

  return (Object.keys(defaultNavConfig) as NavItemKey[]).reduce(
    (resolved, key) => {
      const item = { ...defaultNavConfig[key], ...overrides[key] };

      resolved[key] = { visible: true, ...item };

      return resolved;
    },
    {} as LocalizedNavConfig,
  );
};
